import { LOCATION_CHANGE } from 'connected-react-router'
import { format } from 'date-fns'
import { Epic } from 'redux-observable'
import { of, timer } from 'rxjs'
import { filter, map, mergeMap, pluck, switchMap, takeUntil } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { ROUTES } from 'constants/routes'
import { EpicDependencies, RootState } from 'src/state/types'
import { downloadBase64 } from 'utils/fileUtils'
import { multiUserOperators } from '../../multiUser/state'
import { userApiSelectors } from '../user'
import { ENDPOINTS, PAYMENTS_AUTO_REFRESH_INTERVAL } from './constants'
import * as selectors from './selectors'
import { events, WalletEvent } from './state'
import * as transformers from './transformers'
import {
  TPartyBalances,
  TReferenceLookup,
  TSupportedBanks,
  TSuspenseAccount,
  TWalletTransaction,
  TWireApprovedPayments,
} from './types'

type WalletEpic = Epic<WalletEvent, WalletEvent, RootState, EpicDependencies>

export const apiFetchBalance: WalletEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.balance_request.type)),
    multiUserOperators.filterCurrentAgencyId(state$),
    mergeMap(action =>
      get(ENDPOINTS.BALANCE, state$, action.payload).pipe(
        pluck('response'),
        map((res: TPartyBalances) => events.balance_success(transformers.transformBalance(res), action.payload)),
        catchRestError(action),
      ),
    ),
    tag('wallet/epics/apiFetchBalance'),
  )

/**
 * `switchMap`, so a second payout dispatched while one is in flight cancels the first's
 * success handling. (The previous `switchMap(of(action).pipe(mergeMap(...)))` wrapper was
 * equivalent — the inner `of` emitted exactly once.)
 */
export const apiPayout: WalletEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.payout_request.type)),
    switchMap(action =>
      post(ENDPOINTS.PAYOUT, state$, transformers.transformPayoutRequest(action.payload), {}).pipe(
        pluck('response'),
        map((response: TWalletTransaction) =>
          events.payout_success(transformers.transformPayoutResponse({ ...response, ...action.payload })),
        ),
        catchRestError(action),
      ),
    ),
    tag('wallet/epics/apiPayout'),
  )

export const apiFetchPayments: WalletEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.payments_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.PAYMENTS, state$, action.payload).pipe(
        pluck('response'),
        map(res => events.payments_success(transformers.transformPayments(res))),
        catchRestError(action),
      ),
    ),
    tag('wallet/epics/apiFetchPayments'),
  )

/**
 * Recon only keeps today's approvals that the wallet has not picked up yet, so they are
 * fetched only when the requested period reaches today.
 */
export const apiFetchApprovedPayments: WalletEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.payments_request.type)),
    switchMap(action => {
      const today = format(new Date(), 'yyyy-MM-dd')
      const { end_date } = action.payload
      if (end_date && end_date < today) return of(events.approvedPayments_success([]))
      return get(ENDPOINTS.APPROVED_PAYMENTS, state$, {}).pipe(
        pluck('response'),
        map((res: TWireApprovedPayments) =>
          events.approvedPayments_success(transformers.transformApprovedPayments(res)),
        ),
        catchRestError(action),
      )
    }),
    tag('wallet/epics/apiFetchApprovedPayments'),
  )

/**
 * Refresh the payments screen every interval while a row is still moving
 * (`shouldAutoRefreshPayments`), so approvals and queued payments settle without a reload.
 * It re-dispatches the last requested period so the range never shifts, and `switchMap`
 * restarts the cadence on every request, its own included. Leaving the screen stops it.
 */
export const autoRefreshPayments: WalletEpic = (action$, state$) =>
  action$.pipe(
    filter(isOfType(events.payments_request.type)),
    switchMap(({ payload }) =>
      timer(PAYMENTS_AUTO_REFRESH_INTERVAL, PAYMENTS_AUTO_REFRESH_INTERVAL).pipe(
        filter(() => selectors.shouldAutoRefreshPayments(state$.value)),
        map(() => events.payments_request(payload)),
        takeUntil(
          action$.pipe(
            filter((a: any) => a.type === LOCATION_CHANGE && a.payload?.location?.pathname !== ROUTES.payments),
          ),
        ),
      ),
    ),
    tag('wallet/epics/autoRefreshPayments'),
  )

export const apiDemandPayments: WalletEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.demandPayments_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.DEMAND, state$, action.payload, {}).pipe(
        pluck('response'),
        map(() => events.demandPayments_success(action.payload)),
        catchRestError(action),
      ),
    ),
    tag('wallet/epics/apiDemandPayments'),
  )

export const apiReversePayments: WalletEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.reversePayments_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.REVERSE, state$, action.payload, {}).pipe(
        pluck('response'),
        map(() => events.reversePayments_success(action.payload)),
        catchRestError(action),
      ),
    ),
    tag('wallet/epics/apiReversePayments'),
  )

export const apiTransferFunds: WalletEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.transfer_request.type)),
    mergeMap(action => {
      const { body, params, meta } = action.payload
      return post(ENDPOINTS.TRANSFER, state$, transformers.transformTransferRequest(body), params).pipe(
        pluck('response'),
        map((res: TWalletTransaction | null) =>
          events.transfer_success(transformers.transformTransferResponse({ res, meta })),
        ),
        catchRestError(action),
      )
    }),
    tag('wallet/epics/apiTransferFunds'),
  )

export const apiTransferWalletFunds: WalletEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.walletTransfer_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.WALLET_TRANSFER, state$, transformers.transformWalletTransferRequest(action.payload), {}).pipe(
        pluck('response'),
        map((res: TWalletTransaction) => events.walletTransfer_success(res)),
        catchRestError(action),
      ),
    ),
    tag('wallet/epics/apiTransferWalletFunds'),
  )

export const apiBulkTransferWalletFunds: WalletEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.bulkWalletTransfer_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.BULK_WALLET_TRANSFER, state$, body, params).pipe(
        pluck('response'),
        map((res: { csvBase64: string }) => {
          downloadBase64(res.csvBase64, 'text/csv', `BulkWalletTransfer-${params.fromWalletRef}.csv`)
          return events.bulkWalletTransfer_success(res)
        }),
        catchRestError(action),
      )
    }),
    tag('wallet/epics/apiBulkTransferWalletFunds'),
  )

export const apiRefundAccount: WalletEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.refund_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.REFUND, state$, transformers.transformRefundRequest(body), params).pipe(
        pluck('response'),
        map(res => events.refund_success(transformers.transformRefundResponse(res))),
        catchRestError(action),
      )
    }),
    tag('wallet/epics/apiRefundAccount'),
  )

export const apiWithdraw: WalletEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.withdraw_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.WITHDRAW, state$, transformers.transformWithdrawRequest(body), params).pipe(
        pluck('response'),
        map(res => events.withdraw_success(transformers.transformWithdrawResponse(res))),
        catchRestError(action),
      )
    }),
    tag('wallet/epics/apiWithdraw'),
  )

export const apiConsolidatedWithdraw: WalletEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.consolidatedWithdraw_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(
        ENDPOINTS.CONSOLIDATED_WITHDRAW,
        state$,
        transformers.transformConsolidatedWithdrawRequest(body),
        params,
      ).pipe(
        pluck('response'),
        map(res => events.consolidatedWithdraw_success(transformers.transformConsolidatedWithdrawResponse(res))),
        catchRestError(action),
      )
    }),
    tag('wallet/epics/apiConsolidatedWithdraw'),
  )

export const apiSendPop: WalletEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.pop_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.POP, state$, action.payload, {}).pipe(
        pluck('response'),
        map((res: { request: string }) => events.pop_success(res)),
        catchRestError(action),
      ),
    ),
    tag('wallet/epics/apiSendPop'),
  )

export const apiGetUnallocatedSuspenseAccounts: WalletEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.unallocatedSuspenseAccountss_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.GET_UNALLOCATED_SUSPENSE_ACCOUNTS, state$, {}).pipe(
        pluck('response'),
        map((res: TSuspenseAccount[]) =>
          events.unallocatedSuspenseAccountss_success(
            transformers.transformGetUnallocatedSuspenseAccountsResponse(res),
          ),
        ),
        catchRestError(action),
      ),
    ),
    tag('wallet/epics/apiGetUnallocatedSuspenseAccounts'),
  )

export const apiGetAllocatedSuspenseAccounts: WalletEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.allocatedSuspenseAccounts_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.GET_ALLOCATED_SUSPENSE_ACCOUNTS, state$, {}).pipe(
        pluck('response'),
        map((res: TSuspenseAccount[]) =>
          events.allocatedSuspenseAccounts_success(transformers.transformGetAllocatedSuspenseAccountsResponse(res)),
        ),
        catchRestError(action),
      ),
    ),
    tag('wallet/epics/apiGetAllocatedSuspenseAccount'),
  )

export const apiAllocateSuspenseAccount: WalletEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.allocateSuspenseAccount_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return put(ENDPOINTS.ALLOCATE_SUSPENSE_ACCOUNT, state$, body, params).pipe(
        pluck('response'),
        map(res => events.allocateSuspenseAccount_success(res, params)),
        catchRestError(action),
      )
    }),
    tag('wallet/epics/apiAllocateSuspenseAccount'),
  )

export const apiSaveSuspenseNote: WalletEpic = (action$, state$, { patch, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.saveSuspenseNote_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return patch(ENDPOINTS.SAVE_SUSPENSE_NOTE, state$, body, params).pipe(
        map(() => {
          const savedBy = [
            userApiSelectors.getUserFirstName(state$.value),
            userApiSelectors.getUserLastName(state$.value),
          ]
            .filter(Boolean)
            .join(' ')
          return events.saveSuspenseNote_success({
            ...action.payload,
            savedBy: savedBy || undefined,
            savedAt: new Date().toISOString(),
          })
        }),
        catchRestError(action),
      )
    }),
    tag('wallet/epics/apiSaveSuspenseNote'),
  )

export const apiReferenceLookup: WalletEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.referenceLookup_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.REFERENCE_LOOKUP, state$, action.payload).pipe(
        pluck('response'),
        map((res: { result?: TReferenceLookup } | null) => events.referenceLookup_success(res?.result)),
        catchRestError(action),
      ),
    ),
    tag('wallet/epics/apiReferenceLookup'),
  )

export const apiFetchBanks: WalletEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.banks_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.BANKS, state$, {}).pipe(
        pluck('response'),
        map((res: TSupportedBanks) => events.banks_success(res)),
        catchRestError(action),
      ),
    ),
    tag('wallet/epics/apiFetchBanks'),
  )
